// src/pages/misc/onboarding/perSeatApi.ts
// [HANDOFF] The one client of the per-seat server. Each exported function calls ONE same-origin proxy under api/ (never Supabase
//   directly) and answers an ApiResult: the value the server sent, or its refusal as {code, message, field?}. The screens map codes
//   to their own copy; nothing here is shown. Born with the price read (WU-142 UNIT 2); the application calls (check, submit, quote,
//   state, resume) join it with the screens that need them — their shapes are docs/manifest/WU-138.md § THE CONTRACT AS LANDED in
//   the FE repo (P3, P5 and P6 there); the check's and the submit's shapes are their edges' own headers in the DB repo
//   (edge_functions/check-per-seat-application.ts and submit-per-seat-onboarding.ts), which amend the contract to their truth.
//   Every answer of the server carries `success`; a refusal is {success: false, code, message, …}; the price book's
//   edge, older, says {success: false, error} — both read as a refusal here.

import { CYCLE_MONTHS, billingCycle, extensionCharges } from '../../settings/create-organization/orderQuote';
import type { OrgSetup } from '../../settings/create-organization/orgSetup';
import { PLAN_WORD, type PeriodMonths, type PlanWord, type PriceBook } from './priceBook';
import type { StructureDraft } from './structureDraft';

/** The server's refusal, as every proxy forwards it: a stable code, a message in Spanish, the field it names when it names one. */
export interface ApiRefusal {
  code: string;
  message: string;
  field?: string;
  /** The application the refusal names (ACCOUNT_ALREADY_PAID, CHECKOUT_ALREADY_PAID and every P3, P5 and P6 refusal carry it). */
  applicationId?: string;
}

export type ApiResult<T> = { ok: true; value: T } | { ok: false; refusal: ApiRefusal };

/** One plan row for one period: the Stripe row_key and the amount per seat, per period and per month (period / months, 2 decimals). */
export interface PricingPlanPeriod {
  months: number;
  row_key: string;
  per_seat_per_period_mxn: number;
  per_seat_per_month_mxn: number;
}

export interface PricingPlan {
  plan: string;
  sort_order: number;
  periods: PricingPlanPeriod[];
}

/** One extension row for one period, per unit (a sales user, a team, the organization, the account). */
export interface PricingExtensionPeriod {
  months: number;
  row_key: string;
  per_unit_per_period_mxn: number;
  per_unit_per_month_mxn: number;
}

export interface PricingExtension {
  family: string;
  scope: string;
  /** The plan words this row is sold on: a family can have one row for some plans and another row for the rest. */
  plans: string[];
  requires_family: string | null;
  choice_group: string | null;
  sort_order: number;
  periods: PricingExtensionPeriod[];
}

/** A plan's maximums; null = no plan maximum. */
export interface PricingLimits {
  max_organizations: number | null;
  max_projects: number | null;
  max_teams: number | null;
  max_sales_users: number | null;
  max_admins: number | null;
}

/** The book as served by get_public_per_seat_pricing: MAPPED rows only. A row without its Stripe ids on this environment is absent, never zero (S-9). */
export interface PerSeatPricing {
  plans: PricingPlan[];
  extensions: PricingExtension[];
  limits: Record<string, PricingLimits>;
  plan_words: string[];
}

const PRICING_PROXY = '/api/get-per-seat-pricing';

/** Reads the price book (POST, body {}). A failed read is a refusal the screen shows as its one line (S-9), never a number of its own. */
export async function fetchPerSeatPricing(): Promise<ApiResult<PerSeatPricing>> {
  return postProxy(PRICING_PROXY, {}, (answer) => (isPerSeatPricing(answer.pricing) ? answer.pricing : undefined));
}

function isPerSeatPricing(value: unknown): value is PerSeatPricing {
  if (!isRecord(value)) return false;
  return Array.isArray(value.plans) && Array.isArray(value.extensions) && isRecord(value.limits) && Array.isArray(value.plan_words);
}

const CHECK_PROXY = '/api/check-per-seat-application';

/** The check while the buyer types (P1): is this account name free, is this e-mail free - the submit's own truth, never a guess. */
export interface ApplicationCheck {
  nameAvailable: boolean;
  emailAvailable: boolean;
}

/** Asks whether the account name and the e-mail are free (POST {name, email}); an answer without both booleans is ANSWER_MALFORMED. */
export async function checkPerSeatApplication(name: string, email: string): Promise<ApiResult<ApplicationCheck>> {
  return postProxy(CHECK_PROXY, { name, email }, (answer) => {
    const { nameAvailable, emailAvailable } = answer;
    return typeof nameAvailable === 'boolean' && typeof emailAvailable === 'boolean' ? { nameAvailable, emailAvailable } : undefined;
  });
}

// [HANDOFF] THE PASSWORD HOLDER (S-11): the account step's password lives in this module only - never storage, never the URL - from
//   the step's submit (holdPassword) until P2's submit reads it (heldPassword) and clears it on its answer (clearHeldPassword, UNIT 7).
let held: string | undefined;

export function holdPassword(password: string): void {
  held = password;
}

export function heldPassword(): string | undefined {
  return held;
}

export function clearHeldPassword(): void {
  held = undefined;
}

// [HANDOFF] THE CHECKOUT'S CALLS (WU-142 UNIT 7 CHK1; docs/manifest/WU-142.md SESSION 32 RECORD UNIT 7 THE DESIGN D-C3): the submit
//   (P2) and the quote (P3) through their own proxies, the checkout (P4) through the old flow's proxy api/create-checkout-session.js,
//   which forwards the body whole - the edge reads the per-seat lane's return_to beside application_id and discount_code. The
//   submission is built here from what the screens hold, so the server is sent what the screen priced: the plan word, the period in
//   months and each extension line's row key and quantity (extensionCharges); the server prices them from its own book.
const SUBMIT_PROXY = '/api/submit-per-seat-onboarding';
const QUOTE_PROXY = '/api/per-seat-quote';
const CHECKOUT_PROXY = '/api/create-checkout-session';

/** The person the account step saved, as loadOnboardingPerson() answers it. */
export interface PerSeatPerson {
  first: string;
  last: string;
  email: string;
}

/** P2's request in password mode: the account, its plan and period, the structure as the builder drew it and the extension lines. */
export interface PerSeatSubmission {
  authMode: 'password';
  email: string;
  firstName: string;
  lastName: string;
  password: string;
  name: string;
  plan: PlanWord;
  periodMonths: PeriodMonths;
  structure: Pick<StructureDraft, 'path' | 'name' | 'empresas' | 'proyectos' | 'equipos' | 'asesores' | 'admins' | 'links' | 'assign'>;
  extensions: { rowKey: string; quantity: number }[];
}

/**
 * Builds P2's request from the builder's draft, the order's setup, the account step's person and the held password. The account's
 * name is the draft's; the structure carries the draft's nine fields by name - its savedAt and applicationId never leave the
 * browser. null = THE FAILED READ: a turned-on extension whose row the book lacks, so nothing is sent the screen could not price.
 */
export function perSeatSubmission(
  book: PriceBook,
  draft: StructureDraft,
  setup: OrgSetup,
  person: PerSeatPerson,
  password: string,
): PerSeatSubmission | null {
  const cycle = billingCycle(setup);
  const charges = extensionCharges(book, draft, setup.addons, setup.planId, cycle);
  if (charges === null) return null;
  const { path, name, empresas, proyectos, equipos, asesores, admins, links, assign } = draft;
  return {
    authMode: 'password',
    email: person.email,
    firstName: person.first,
    lastName: person.last,
    password,
    name,
    plan: PLAN_WORD[setup.planId],
    periodMonths: CYCLE_MONTHS[cycle],
    structure: { path, name, empresas, proyectos, equipos, asesores, admins, links, assign },
    extensions: charges.map(({ rowKey, quantity }) => ({ rowKey, quantity })),
  };
}

/** P2's answer: the application it opened, the account it belongs to and the application's state. */
export interface PerSeatApplication {
  applicationId: string;
  accountId: string;
  state: string;
}

/** Submits the application (P2, POST the submission); ACCOUNT_ALREADY_PAID's refusal names the paid application. */
export async function submitPerSeatOnboarding(body: PerSeatSubmission): Promise<ApiResult<PerSeatApplication>> {
  return postProxy(SUBMIT_PROXY, body, (answer) => {
    const applicationId = text(answer.applicationId);
    const accountId = text(answer.accountId);
    const state = text(answer.state);
    if (applicationId === undefined || accountId === undefined || state === undefined) return undefined;
    return { applicationId, accountId, state };
  });
}

/** One line of P3's quote, as per-seat-quote.ts types it: amounts in MXN, the quantity a positive integer. */
export interface PerSeatQuoteLine {
  rowKey: string;
  kind: string;
  family: string;
  scope: string;
  unitAmountMxn: number;
  quantity: number;
  amountMxn: number;
}

/** A valid code's effect on P3's quote: its type and value, the amount it takes off and the total after it (MXN). */
export interface PerSeatQuoteDiscount {
  code: string;
  type: 'percent' | 'fixed_amount';
  value: number;
  amountOffMxn: number;
  totalAfterMxn: number;
}

/** P3's quote of an application, priced by the server from its own book: what Stripe will charge. */
export interface PerSeatQuote {
  plan: string;
  periodMonths: number;
  seats: number;
  lines: PerSeatQuoteLine[];
  totalMxn: number;
  discount: PerSeatQuoteDiscount | null;
}

/** Reads the quote of an application (P3, POST {applicationId, discountCode}); an answer off the quote's shape is ANSWER_MALFORMED. */
export async function fetchPerSeatQuote(applicationId: string, discountCode?: string): Promise<ApiResult<PerSeatQuote>> {
  return postProxy(QUOTE_PROXY, { applicationId, discountCode }, (answer) => {
    const plan = text(answer.plan);
    const lines = quoteLines(answer.lines);
    const discount = quoteDiscount(answer.discount);
    const { periodMonths, seats, totalMxn } = answer;
    if (plan === undefined || lines === undefined || discount === undefined) return undefined;
    if (!isAmount(periodMonths) || !isAmount(seats) || !isAmount(totalMxn)) return undefined;
    return { plan, periodMonths, seats, lines, totalMxn, discount };
  });
}

/** The quote's lines, each of its shape; one line off its shape makes the whole list undefined. */
function quoteLines(value: unknown): PerSeatQuoteLine[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const lines: PerSeatQuoteLine[] = [];
  for (const item of value) {
    const line = quoteLine(item);
    if (line === undefined) return undefined;
    lines.push(line);
  }
  return lines;
}

function quoteLine(value: unknown): PerSeatQuoteLine | undefined {
  if (!isRecord(value)) return undefined;
  const rowKey = text(value.rowKey);
  const kind = text(value.kind);
  const family = text(value.family);
  const scope = text(value.scope);
  const { unitAmountMxn, quantity, amountMxn } = value;
  if (rowKey === undefined || kind === undefined || family === undefined || scope === undefined) return undefined;
  if (!isAmount(unitAmountMxn) || !isAmount(amountMxn) || !isAmount(quantity)) return undefined;
  if (!Number.isInteger(quantity) || quantity < 1) return undefined;
  return { rowKey, kind, family, scope, unitAmountMxn, quantity, amountMxn };
}

/** The quote's discount: null when no code applied, undefined when it is off its shape. */
function quoteDiscount(value: unknown): PerSeatQuoteDiscount | null | undefined {
  if (value === null) return null;
  if (!isRecord(value)) return undefined;
  const code = text(value.code);
  const { type, amountOffMxn, totalAfterMxn } = value;
  const off = value.value;
  if (code === undefined || !isDiscountType(type)) return undefined;
  if (!isAmount(off) || !isAmount(amountOffMxn) || !isAmount(totalAfterMxn)) return undefined;
  return { code, type, value: off, amountOffMxn, totalAfterMxn };
}

function isDiscountType(value: unknown): value is PerSeatQuoteDiscount['type'] {
  return value === 'percent' || value === 'fixed_amount';
}

/** P4's answer: the Stripe Checkout page the buyer is sent to. */
export interface PerSeatCheckout {
  checkoutUrl: string;
}

/**
 * Opens the Stripe Checkout of an application (P4, POST {application_id, discount_code, return_to}); returnTo is the page the
 * server sends the buyer back to with its status and the application. JSON.stringify omits a code left undefined.
 */
export async function startPerSeatCheckout(
  applicationId: string,
  discountCode: string | undefined,
  returnTo: string,
): Promise<ApiResult<PerSeatCheckout>> {
  const body = { application_id: applicationId, discount_code: discountCode, return_to: returnTo };
  return postProxy(CHECKOUT_PROXY, body, checkoutAnswer);
}

/**
 * Opens the Stripe Checkout of a legacy application (WU-145 U3-A; docs/manifest/WU-145.md SESSION 5 RECORD THE SELF-STAMPS 12):
 * the old view's body exactly, POST {application_id, discount_code}, with no return_to - the edge charges the old lane as before
 * and Stripe returns the buyer to the old lane's `/?status=...&application_id=...`. JSON.stringify omits a code left undefined.
 */
export async function startLegacyCheckout(
  applicationId: string,
  discountCode: string | undefined,
): Promise<ApiResult<PerSeatCheckout>> {
  return postProxy(CHECKOUT_PROXY, { application_id: applicationId, discount_code: discountCode }, checkoutAnswer);
}

/** P4's answer picked for both lanes: the Stripe page, or undefined when the answer carries none. */
function checkoutAnswer(answer: Answer): PerSeatCheckout | undefined {
  const checkoutUrl = text(answer.checkoutUrl);
  return checkoutUrl === undefined ? undefined : { checkoutUrl };
}

// [HANDOFF] THE RETURN'S AND THE RESUME'S CALLS (WU-142 UNIT 8 RES1; docs/manifest/WU-142.md SESSION 39 RECORD UNIT 8 THE DESIGN
//   D-R3): the state (P5) and the resume (P6) through their own proxies, each a POST of {applicationId}. P5 answers the
//   application's lane, its state as stored (never narrowed: the column holds no CHECK), whether its account is ready, the login
//   and a failed state's support message; P6 answers P5's fields and, for a per-seat application, its quote flat beside them -
//   split here into the state and the quote, the quote null for the legacy lane, so each page reads one shape per question.
const STATE_PROXY = '/api/per-seat-application-state';
const RESUME_PROXY = '/api/resume-onboarding';

/** P5's answer: the lane, the state as stored, whether the account is ready, the login, a failed state's support message. */
export interface PerSeatState {
  applicationId: string;
  lane: 'legacy' | 'per_seat';
  state: string;
  accountReady: boolean;
  loginUrl: string;
  supportMessage?: string;
}

/** Reads the state of an application (P5, POST {applicationId}); an answer off the state's shape is ANSWER_MALFORMED. */
export async function fetchPerSeatApplicationState(applicationId: string): Promise<ApiResult<PerSeatState>> {
  return postProxy(STATE_PROXY, { applicationId }, applicationState);
}

/** P6's answer: the state as P5 answers it and the quote, P3's shape without a discount - null for the legacy lane. */
export interface PerSeatResume {
  status: PerSeatState;
  quote: Omit<PerSeatQuote, 'discount'> | null;
}

/** Reads the resume of an application (P6, POST {applicationId}); a per-seat answer without its quote is ANSWER_MALFORMED. */
export async function fetchPerSeatResume(applicationId: string): Promise<ApiResult<PerSeatResume>> {
  return postProxy(RESUME_PROXY, { applicationId }, (answer) => {
    const status = applicationState(answer);
    if (status === undefined) return undefined;
    if (status.lane === 'legacy') return { status, quote: null };
    const plan = text(answer.plan);
    const lines = quoteLines(answer.lines);
    const { periodMonths, seats, totalMxn } = answer;
    if (plan === undefined || lines === undefined) return undefined;
    if (!isAmount(periodMonths) || !isAmount(seats) || !isAmount(totalMxn)) return undefined;
    return { status, quote: { plan, periodMonths, seats, lines, totalMxn } };
  });
}

/** P5's fields as P5 answers them and P6 carries them flat; one field off its shape makes the whole state undefined. */
function applicationState(value: unknown): PerSeatState | undefined {
  if (!isRecord(value)) return undefined;
  const applicationId = text(value.applicationId);
  const state = text(value.state);
  const loginUrl = text(value.loginUrl);
  const { lane, accountReady, supportMessage } = value;
  if (applicationId === undefined || state === undefined || loginUrl === undefined) return undefined;
  if (!isLane(lane) || typeof accountReady !== 'boolean') return undefined;
  if (supportMessage === undefined) return { applicationId, lane, state, accountReady, loginUrl };
  const message = text(supportMessage);
  if (message === undefined) return undefined;
  return { applicationId, lane, state, accountReady, loginUrl, supportMessage: message };
}

function isLane(value: unknown): value is PerSeatState['lane'] {
  return value === 'legacy' || value === 'per_seat';
}

type Answer = Record<string, unknown>;

function isRecord(value: unknown): value is Answer {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function text(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function isAmount(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function refused(code: string, message: string, field?: string, applicationId?: string): ApiResult<never> {
  const refusal: ApiRefusal = { code, message };
  if (field !== undefined) refusal.field = field;
  if (applicationId !== undefined) refusal.applicationId = applicationId;
  return { ok: false, refusal };
}

/**
 * POSTs a JSON body to one proxy and reads the server's answer. `pick` takes the value out of a successful answer (`success: true`
 * and a value of the shape the caller knows). An answer that is not ok, not successful or not of that shape is a refusal: the
 * server's code and message when it sent them, else a code naming what failed and the HTTP status.
 */
async function postProxy<T>(path: string, body: unknown, pick: (answer: Answer) => T | undefined): Promise<ApiResult<T>> {
  const response = await fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  }).catch((error: unknown) => (error instanceof Error ? error : new Error(String(error))));
  if (response instanceof Error) {
    return refused('NETWORK_ERROR', `${path}: ${response.message}`);
  }
  const parsed: unknown = await response.json().catch(() => null);
  const answer: Answer = isRecord(parsed) ? parsed : {};
  if (response.ok && answer.success === true) {
    const value = pick(answer);
    if (value !== undefined) return { ok: true, value };
    return refused('ANSWER_MALFORMED', `${path}: the answer carries no value of the expected shape`);
  }
  const code = text(answer.code) ?? `HTTP_${response.status}`;
  const message = text(answer.message) ?? text(answer.error) ?? `${path} answered ${response.status}`;
  return refused(code, message, text(answer.field), text(answer.applicationId));
}
